<template>
  <aside class="rail">
    <a v-if="!hideBackLink" :href="homeUrl" class="rail-back">
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
      <span>Back to event page</span>
    </a>
    <div class="brand-mark"><em>{{ event.brand }}</em> registration</div>
    <div
      class="hero"
      :class="{ 'hero--logo': !!event.logo_url, 'hero--bleed': logoBleed }"
      :style="logoBg ? { backgroundColor: logoBg } : null"
    >
      <img v-if="event.logo_url" class="hero-img" :src="event.logo_url" :alt="event.name || ''" />
      <svg v-else aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" style="color:white;"><path d="M4 5h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2"/><path d="M16 3v4M8 3v4M2 11h20"/></svg>
    </div>
    <div class="event-meta">
      <div class="event-icon">
        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4M16 2v4M3 10h18M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
      </div>
      <div>
        <div class="event-name">{{ event.name }}</div>
        <div class="event-detail" v-if="event.dates">
          <svg aria-hidden="true" style="min-width: 12px;" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4M16 2v4M3 10h18M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
          {{ event.dates }}
        </div>
        <div class="event-detail" v-if="event.time_range">
          <svg aria-hidden="true" style="min-width: 12px;" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
          {{ event.time_range }}
        </div>
        <div class="event-detail" v-if="event.location">
          <!-- Online (virtual) events get the globe used on the landing page;
               in-person / hybrid keep the map-pin for their physical venue. -->
          <svg v-if="event.event_format === 'virtual'" aria-hidden="true" style="min-width: 12px;" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          <svg v-else aria-hidden="true" style="min-width: 12px;" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
          {{ event.location }}
        </div>
      </div>
    </div>
    <div class="rail-actions">
      <button v-if="showAgenda" type="button" class="agenda-btn" @click="$emit('open-agenda')">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5h11M9 12h11M9 19h11M5 5v.01M5 12v.01M5 19v.01"/></svg>
        <span>{{ agendaLabel }}</span>
        <svg aria-hidden="true" class="arr" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17l10-10M8 7h9v9"/></svg>
      </button>
      <button v-if="showCe" type="button" class="agenda-btn" @click="$emit('open-ce')">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 13l2 2 4-4"/></svg>
        <span>CE Info</span>
        <svg aria-hidden="true" class="arr" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17l10-10M8 7h9v9"/></svg>
      </button>
    </div>
    <div v-if="hasCoupons" class="rail-discount">
      <label class="rail-discount-label" for="rail-discount-input">Discount code</label>
      <div class="rail-discount-row">
        <input autocomplete="off" type="text" id="rail-discount-input" placeholder="Enter code" v-model="discount" @keyup.enter="apply" />
        <button type="button" class="rail-discount-apply" @click="apply">Apply</button>
      </div>
      <div v-if="couponError" class="rail-discount-error">{{ couponError }}</div>
    </div>
    <div class="order">
      <div class="order-label">Order summary</div>
      <div class="order-lines">
        <div v-if="!flatLines.tickets.length && !flatLines.addons.length" class="empty">No tickets selected</div>
        <template v-else>
          <div v-for="(l, i) in flatLines.tickets" :key="'t' + i" class="line" :class="{ discount: l.type === 'discount' }">
            <span>{{ truncate(l.name) }}</span>
            <span>{{ l.type === 'discount' ? '−' : '' }}{{ currencySymbol }}{{ money(l.price) }}</span>
          </div>
          <div v-if="flatLines.addons.length" class="order-subhead">Add-ons</div>
          <div v-for="(l, i) in flatLines.addons" :key="'a' + i" class="line">
            <span>{{ truncate(l.name) }}</span>
            <span>{{ currencySymbol }}{{ money(l.price) }}</span>
          </div>
          <div v-if="donation > 0" class="line">
            <span>Donation</span>
            <span>{{ currencySymbol }}{{ money(donation) }}</span>
          </div>
        </template>
      </div>
      <div v-if="sessionsSummary" class="order-sessions">{{ sessionsSummary }}</div>
      <div class="order-total"><span>Total</span><span>{{ currencySymbol }}{{ money(total) }}</span></div>

      <div v-if="donationEnabled" class="rail-donation">
        <label class="rail-discount-label" for="rail-donation-input">{{ donationTitle }}</label>
        <div class="rail-discount-row">
          <input autocomplete="off" type="number" min="0" step="1" id="rail-donation-input" placeholder="0" :value="donation" @input="$emit('update-donation', Number($event.target.value) || 0)" />
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
export default {
  name: 'lp-rail',
  props: {
    event: { type: Object, required: true },
    history: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    currencySymbol: { type: String, default: '$' },
    sessionsSummary: { type: String, default: '' },
    // Default true so embed/widget entry points without a fresh payload keep
    // the button; the page explicitly passes false when Hide Sessions is on.
    showAgenda: { type: Boolean, default: true },
    // Single-session events open the session drawer, not the agenda list, so
    // the page passes "View session details" for them.
    agendaLabel: { type: String, default: 'View full agenda' },
    showCe: { type: Boolean, default: true },
    hasCoupons: { type: Boolean, default: false },
    couponError: { type: String, default: '' },
    donationEnabled: { type: Boolean, default: false },
    donationTitle: { type: String, default: 'Add a donation' },
    donation: { type: Number, default: 0 },
    // Hidden when the page is opened as a standalone/embedded entry point
    // (direct link or iframe widget) — there is no event page to return to.
    hideBackLink: { type: Boolean, default: false },
  },
  data() {
    return {
      discount: '',
      // Resolved by probeLogoBackground(): the logo's own backdrop color when it
      // has one, and whether it should run edge-to-edge instead of sitting in a
      // padded card. Empty/false = neutral white card (safe default).
      logoBg: '',
      logoBleed: false,
    }
  },
  mounted() {
    this.probeLogoBackground(this.event.logo_url)
  },
  watch: {
    'event.logo_url'(url) {
      this.logoBg = ''
      this.logoBleed = false
      this.probeLogoBackground(url)
    },
  },
  computed: {
    // Back-to-home target. Registration lives at /{pathname}/registration, so
    // stripping the trailing /registration returns the event landing home.
    // Query params (e.g. the theme-preview ?thema=…) are preserved.
    homeUrl() {
      const base = window.location.pathname.replace(/\/registration\/?$/, '') || '/'
      return base + window.location.search
    },
    // order.history is nested: [{ name, history: [{ name, price, type }] }].
    // Flatten into ticket/discount lines and add-on lines (shown under a
    // separate sub-header, like the order summary mockup).
    flatLines() {
      const tickets = []
      const addons = []
      ;(this.history || []).forEach(group => {
        ;(group.history || []).forEach(item => {
          if (item.type === 'addon') addons.push(item)
          else tickets.push(item)
        })
      })
      return { tickets, addons }
    },
  },
  methods: {
    truncate(name) {
      return name.length > 30 ? name.slice(0, 30) + '…' : name
    },
    money(n) {
      return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    apply() {
      if (this.discount.trim()) this.$emit('apply-coupon', this.discount.trim())
    },
    /**
     * CSS cannot tell a transparent logo from one with a baked-in backdrop, and
     * the two need opposite treatments: transparent needs a neutral card so the
     * mark stays readable, baked-in needs the card to disappear or match, or you
     * get a visible box-inside-a-box. So sample the image's border pixels once
     * it loads and pick one of three modes:
     *
     *   transparent edges     -> keep the white card (logoBg stays empty)
     *   opaque, one flat color -> card takes that color, so there is no seam
     *   opaque, varied        -> full-bleed, the image IS the card
     *
     * Probed through a separate CORS-enabled Image so a missing
     * Access-Control-Allow-Origin can never break the visible logo — a tainted
     * or failed probe just leaves the white-card default in place.
     */
    probeLogoBackground(url) {
      if (!url || typeof document === 'undefined') return

      const probe = new Image()
      probe.crossOrigin = 'anonymous'
      probe.onload = () => {
        const samples = this.sampleEdgePixels(probe)
        if (!samples) return

        // Any see-through edge means the mark relies on the page background.
        if (samples.some(px => px[3] < 250)) return

        const flat = samples.every(px =>
          Math.abs(px[0] - samples[0][0]) <= 10 &&
          Math.abs(px[1] - samples[0][1]) <= 10 &&
          Math.abs(px[2] - samples[0][2]) <= 10
        )

        if (flat) this.logoBg = `rgb(${samples[0][0]}, ${samples[0][1]}, ${samples[0][2]})`
        else this.logoBleed = true
      }
      probe.src = url
    },
    /**
     * Read the 8 border pixels (4 corners + 4 edge midpoints) of the image.
     * Downscaled to 24px first so a single stray antialiased pixel cannot decide
     * the outcome. Returns null when the canvas is unreadable (tainted / no 2d).
     */
    sampleEdgePixels(img) {
      const S = 24
      const canvas = document.createElement('canvas')
      canvas.width = S
      canvas.height = S
      const ctx = canvas.getContext('2d')
      if (!ctx) return null

      try {
        ctx.drawImage(img, 0, 0, S, S)
        const m = Math.floor(S / 2)
        const e = S - 1
        return [[0, 0], [e, 0], [0, e], [e, e], [m, 0], [m, e], [0, m], [e, m]]
          .map(([x, y]) => Array.from(ctx.getImageData(x, y, 1, 1).data))
      } catch (err) {
        return null
      }
    },
  },
}
</script>

<style lang="scss">
/* ============ LEFT RAIL ============ */
aside.rail {
  background: var(--brand-deep);
  color: white;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: sticky;
  top: 0;
  height: calc(100vh - var(--footer-h));
  overflow-y: auto;
  overscroll-behavior: contain;
}
.rail .rail-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  color: white;
  opacity: 0.8;
  font-size: var(--fs-small);
  font-weight: 500;
  text-decoration: none;
  transition: opacity 0.15s, transform 0.15s;
}
.rail .rail-back:hover { opacity: 1; }
.rail .rail-back:hover svg { transform: translateX(-2px); }
.rail .rail-back svg { flex-shrink: 0; transition: transform 0.15s; }
.rail .brand-mark { font-size: var(--fs-body); font-weight: 500; }
.rail .brand-mark em { font-style: italic; font-weight: 400; }
.rail .hero {
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, var(--brand) 0%, var(--brand-light) 100%);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  min-height: 170px;
  /* Lets the full-bleed variant be clipped by the card radius instead of
     needing its own matching border-radius on the image. */
  overflow: hidden;
}
/* Organizer logos are usually transparent PNG/SVG, so a brand-colored backdrop
   makes them unreadable (dark logo on dark brand). Give the logo a neutral card
   instead — the gradient stays only as the no-logo fallback art. Hard-coded
   white on purpose: --surface is organizer-customizable, so it cannot guarantee
   the neutrality this fix exists for. probeLogoBackground() may replace this
   background inline when the logo carries its own flat backdrop; the transition
   keeps that swap from popping once the image resolves. */
.rail .hero--logo {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 20px 24px;
  min-height: 140px;
  /* The inner border defines a light card; the outer white ring defines a dark
     one, so the card never dissolves into the rail whatever color the sampled
     logo backdrop turns out to be. */
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.12);
  transition: background-color 0.2s ease, padding 0.2s ease;
}
/* Logo with a non-uniform opaque backdrop (photo, gradient, framed artwork):
   no card can sit behind that without reading as a box in a box, so the image
   becomes the card. */
.rail .hero--bleed {
  padding: 0;
  border: 0;
  min-height: 0;
  background: transparent;
}
.rail .hero svg { width: 56px; height: 56px; opacity: 0.9; }
/* Intrinsic-size contain (same approach as .ce-body-logo img): wide wordmarks
   are width-bound, tall square marks height-bound, neither is upscaled and
   neither reaches the card edge. */
.rail .hero-img {
  max-width: 100%;
  max-height: 100px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}
.rail .hero--bleed .hero-img {
  width: 100%;
  max-height: 200px;
}
.rail .event-meta {
  display: flex; gap: 12px; align-items: flex-start;
  padding-bottom: 16px;
  border-bottom: 0.5px solid rgba(255,255,255,0.18);
}
.rail .event-icon {
  width: 40px; height: 40px; border-radius: var(--radius-md);
  background: rgba(255,255,255,0.15); flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.rail .event-name { font-size: var(--fs-body-lg); font-weight: 500; line-height: 1.4; }
.rail .event-detail {
  font-size: var(--fs-label);
  opacity: 0.75;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.rail .agenda-btn {
  background: rgba(255,255,255,0.10);
  color: white;
  border: 0.5px solid rgba(255,255,255,0.22);
  padding: 12px 14px;
  border-radius: var(--radius-md);
  font-size: var(--fs-small);
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: inherit;
  transition: background 0.15s;
}
.rail .agenda-btn:hover { background: rgba(255,255,255,0.18); }
.rail .agenda-btn .arr { margin-left: auto; opacity: 0.7; }
.rail-actions { display: flex; flex-direction: column; gap: 8px; }
.rail .order {
  margin-top: auto;
  padding-top: 18px;
  border-top: 0.5px solid rgba(255,255,255,0.18);
}
.rail .order-label {
  font-size: var(--fs-caption);
  opacity: 0.7;
  margin-bottom: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.rail .order-lines { font-size: var(--fs-label); line-height: 1.7; }
.rail .order-lines .empty { opacity: 0.6; font-style: italic; }
.rail .order-lines .line { display: flex; justify-content: space-between; gap: 8px; padding: 2px 0; }
.rail .order-lines .line span:first-child { opacity: 0.85; }
.rail .order-subhead {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 0.5px dashed rgba(255,255,255,0.18);
  font-size: var(--fs-micro);
  opacity: 0.6;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.rail .order-lines .line.discount { color: #9ff5c4; }
.rail .order-sessions {
  font-size: var(--fs-caption);
  opacity: 0.7;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 0.5px dashed rgba(255,255,255,0.18);
}
.rail .order-total {
  border-top: 0.5px solid rgba(255,255,255,0.18);
  margin-top: 12px;
  padding-top: 12px;
  display: flex;
  justify-content: space-between;
  font-size: var(--fs-body);
  font-weight: 500;
}
.rail-discount, .rail-donation {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 0.5px solid rgba(255,255,255,0.18);
}
/* The discount block sits above the order summary. The rail is a flex column
   whose bottom-pinning comes from `margin-top: auto` on .order — hand that
   auto margin to the discount block so the two stay glued together at the
   bottom instead of the free space opening up between them. When there are no
   coupons the block isn't rendered and .order keeps the auto margin itself. */
.rail > .rail-discount { margin-top: auto; }
.rail > .rail-discount + .order { margin-top: 0; }
.rail-discount-label {
  font-size: var(--fs-caption);
  opacity: 0.7;
  margin-bottom: 8px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  display: block;
}
.rail-discount-row {
  display: flex;
  gap: 6px;
}
.rail-discount-row input {
  flex: 1;
  background: rgba(255,255,255,0.08);
  border: 0.5px solid rgba(255,255,255,0.22);
  color: white;
  padding: 8px 10px;
  border-radius: var(--radius-sm);
  font-size: var(--fs-label);
  font-family: inherit;
  min-width: 0;
}
.rail-discount-row input::placeholder { color: rgba(255,255,255,0.45); }
.rail-discount-row input:focus {
  outline: none;
  border-color: rgba(255,255,255,0.55);
  background: rgba(255,255,255,0.14);
  box-shadow: none;
}
.rail-discount-apply {
  background: rgba(255,255,255,0.14);
  border: 0.5px solid rgba(255,255,255,0.22);
  color: white;
  padding: 8px 12px;
  border-radius: var(--radius-sm);
  font-size: var(--fs-label);
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition: background 0.15s;
}
.rail-discount-apply:hover { background: rgba(255,255,255,0.22); }
.rail-discount-error {
  font-size: var(--fs-caption);
  color: #ffb4b4;
  margin-top: 6px;
}

/* Mobile: the page collapses to a single column, so the rail must flow with
   the page instead of being a fixed-height internal scroll container. Without
   this it stays `position: sticky; height: calc(100vh - footer); overflow: auto`,
   which captures the scroll gesture and overlaps the content below it. */
@media (max-width: 800px) {
  aside.rail {
    position: static;
    height: auto;
    overflow-y: visible;
  }
}
/* Phones: no rail at all — the page carries a compact event banner, the
   agenda/CE links, the discount field and the footer total instead. */
@media (max-width: 640px) {
  aside.rail { display: none; }
}
</style>
